/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { RenderingDetails, SavingsLineItemData, SavingsRightSectionType } from "@swiggy-private/cart-types";
import { AnalyticsContext, getBridgeSdk } from "@swiggy-private/checkout-core";
import { NavigationTypes } from "@swiggy-private/native-bridge";
import { isEmpty } from "@swiggy-private/js-utils";

// analytics
import {
    clickSavingsCornerHabitViewReward,
    impressionSavingsCornerHabitRewardUnlock,
    impressionSavingsCornerLineItem,
} from "@cart-client/analytics/events";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// helpers
import { getCartId } from "@cart-client/helpers/Cart";

export interface UseHabitFormingDataReturnType {
    getHabitFormingLineItems: () => SavingsLineItemData[];
}

export const useHabitFormingData = (habitFormingRenderingDetails?: RenderingDetails): UseHabitFormingDataReturnType => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const cartId = cartResponse ? getCartId(cartResponse) : "";

    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const habitformingDetails = useMemo(() => {
        const callout = habitFormingRenderingDetails?.meta?.callout;
        const additionalCallout = habitFormingRenderingDetails?.meta?.additionalCallout;

        return {
            title: callout?.description ? `${callout?.heading} - ${callout.description}` : callout?.heading,
            subtitle: additionalCallout?.description,
            ctaLink: callout?.cta?.link,
            icon: callout?.icon,
        };
    }, [habitFormingRenderingDetails?.meta?.additionalCallout, habitFormingRenderingDetails?.meta?.callout]);

    const { title, subtitle, ctaLink, icon } = habitformingDetails;

    const onClick = useCallback(() => {
        if (isEmpty(ctaLink)) {
            return;
        }

        clickSavingsCornerHabitViewReward(clickEvent, cartId);
        getBridgeSdk()
            .navigateToScreen({
                type: NavigationTypes.DEEPLINK,
                link: ctaLink,
            })
            .catch(() => void 0);
    }, [cartId, clickEvent, ctaLink]);

    const onLineItemLoad = useCallback(() => {
        impressionSavingsCornerLineItem(impressionEvent, cartId, {
            headerString: title,
            subHeadString: subtitle,
        });

        if (!isEmpty(subtitle)) {
            impressionSavingsCornerHabitRewardUnlock(impressionEvent, cartId, {
                habit_form_title: title,
                habit_form_subtitle: subtitle,
            });
        }
    }, [cartId, impressionEvent, subtitle, title]);

    const habitFormingLineItem: SavingsLineItemData | null = useMemo(() => {
        if (isEmpty(title)) {
            return null;
        }

        const habitformingLineItemData: SavingsLineItemData = {
            icon,
            title: {
                value: title,
            },
            rightSection: {
                type: SavingsRightSectionType.ICON,
            },
            onLoad: onLineItemLoad,
            onLineItemClick: onClick,
        };

        if (!isEmpty(subtitle)) {
            habitformingLineItemData["subtitle"] = {
                value: subtitle,
            };
        }

        return habitformingLineItemData;
    }, [icon, onClick, onLineItemLoad, subtitle, title]);

    const getHabitFormingLineItems = useCallback(() => {
        if (habitFormingLineItem == null) {
            return [];
        }
        return [habitFormingLineItem];
    }, [habitFormingLineItem]);

    return { getHabitFormingLineItems };
};
